import React, { useEffect, useState } from "react";
import { api, INTEREST_LABELS } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, Ticket, TrendingUp, CalendarClock } from "lucide-react";

const KPI = [
  { key: "total_subscribers", label: "Összes feliratkozó", icon: Users, testId: "admin-kpi-total" },
  { key: "today_subscribers", label: "Mai új", icon: CalendarClock, testId: "admin-kpi-today" },
  { key: "coupons_issued", label: "Kiadott kuponok", icon: Ticket, testId: "admin-kpi-coupons" },
  { key: "conversion_rate", label: "Beváltási arány", icon: TrendingUp, testId: "admin-kpi-conversion", suffix: "%" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-[#5C524A]">Betöltés...</p>;
  if (!stats) return <p className="text-[#5C524A]">Nem sikerült betölteni az adatokat.</p>;

  const hasData = stats.total_subscribers > 0;

  return (
    <div className="space-y-8">
      <div>
        <p className="section-label">Áttekintés</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#201B18] mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI.map((k) => {
          const Icon = k.icon;
          const value = stats[k.key] ?? 0;
          return (
            <div key={k.key} data-testid={k.testId} className="rounded-2xl bg-white border border-[#2C221E]/10 p-5">
              <div className="flex items-start justify-between">
                <p className="section-label">{k.label}</p>
                <Icon className="h-4 w-4 text-[#8C8075]" />
              </div>
              <p className="mt-3 font-mono-brand text-3xl font-bold text-[#2C221E]">
                {value}{k.suffix || ""}
              </p>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl bg-white border border-[#2C221E]/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="section-label">Feliratkozások · utolsó 30 nap</p>
            <p className="text-sm text-[#5C524A] mt-1">{stats.week_subscribers} új az elmúlt héten · {stats.month_subscribers} havonta</p>
          </div>
        </div>
        {hasData ? (
          <div className="h-64 w-full">
            <ResponsiveContainer>
              <LineChart data={stats.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3EDE2" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8C8075" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#8C8075" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#FBF8F3", border: "1px solid rgba(44,34,30,0.1)", borderRadius: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#4A6B53" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyBlock label="Még nincs adat" />
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-white border border-[#2C221E]/10 p-5">
          <p className="section-label mb-4">Érdeklődési szintek</p>
          {hasData ? (
            <div className="space-y-3">
              {Object.entries(stats.interest_breakdown).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-[#5C524A]">{INTEREST_LABELS[k] || k}</span>
                  <div className="flex items-center gap-3 flex-1 max-w-[200px]">
                    <div className="flex-1 h-1.5 bg-[#F3EDE2] rounded-full overflow-hidden">
                      <div className="h-full bg-[#4A6B53]" style={{ width: `${stats.total_subscribers ? (v / stats.total_subscribers) * 100 : 0}%` }} />
                    </div>
                    <span className="font-mono-brand text-sm font-bold text-[#2C221E] w-6 text-right">{v}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyBlock label="Még nincs adat" />
          )}
        </div>

        <div className="rounded-2xl bg-white border border-[#2C221E]/10 p-5">
          <p className="section-label mb-4">Kampányforrások</p>
          {stats.campaign_sources?.length ? (
            <div className="space-y-3">
              {stats.campaign_sources.map((s) => (
                <div key={s.source} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-[#5C524A] font-mono-brand">{s.source}</span>
                  <span className="font-mono-brand text-sm font-bold text-[#2C221E]">{s.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyBlock label="Még nincs adat" />
          )}
        </div>
      </div>

      <ReferralBlock />
    </div>
  );
}

function ReferralBlock() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get("/admin/referrals").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <div className="rounded-2xl bg-[#2C221E] text-[#FBF8F3] border border-[#C29B78]/30 p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono-brand text-[11px] tracking-[0.25em] text-[#C29B78] uppercase">● Ajánlói rendszer</p>
        <p className="font-mono-brand text-xs text-[#D7CFC6]">{data.total_referred_signups} ajánlott feliratkozó</p>
      </div>
      {data.top_referrers?.length ? (
        <ul className="divide-y divide-white/10">
          {data.top_referrers.map((r) => (
            <li key={r.code} className="py-2.5 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-mono-brand text-xs font-bold">{r.code}</p>
                <p className="text-[11px] text-[#D7CFC6] truncate">{r.subscriber_email}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono-brand text-lg font-bold text-[#C29B78]">×{r.referrals_count}</p>
                <p className="text-[10px] text-[#D7CFC6]">{r.discount_percent}% kupon</p>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-[#D7CFC6]">Még nincs ajánlás. Oszd meg a feliratkozási linket!</p>
      )}
    </div>
  );
}

function EmptyBlock({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-[#2C221E]/15 bg-[#F3EDE2]/40 p-8 text-center">
      <p className="section-label">{label}</p>
      <p className="mt-2 text-sm text-[#8C8075]">A valódi adatok itt jelennek meg az első feliratkozások után.</p>
    </div>
  );
}
